import React, { useState, useEffect } from 'react';
import { Steps } from 'antd';
import { PickerPlatform } from '../steps/StepSelectPlatform';
import { FormConsent } from '../steps/StepConsent';
import { FormDonationOptions } from '../steps/StepDonationOptions';
import { DonationReview } from '../steps/StepDonationReview';
import { PageUpload } from '../steps/StepUpload';
import { FormSurvey } from '../steps/StepSurvey';
import { PageDone } from '../steps/StepDone';

import { FetchFromBackend, ErrorConnectionFailed } from '../../shared/HelperContactBackend';
import '../../styles/App.css';

export const PageDataDonation = () => {

  const [platform, setPlatform] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const [sessionId, setSessionId] = useState(null);
  const [emailIdentifier, setEmailIdentifier] = useState("");

  const [amountToBePayed, setAmountToBePayed] = useState(0);
  const [donationOptions, setDonationOptions] = useState(null);
  const [donationOptionsSet, setDonationOptionsSet] = useState(false);
  const [userData, setUserData] = useState(null);

  // survey related states
  const [demographicSurveyQuestions, setDemographicSurveyQuestions] = useState(null);
  const [usageSurveyQuestions, setUsageSurveyQuestions] = useState(null);
  const [techsavvinessSurveyQuestions, setTechsavvinessSurveyQuestions] = useState(null);
  // The user has to be shown the questions again if different platform is selected 
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyLoadedForPlatform, setSurveyLoadedForPlatform] = useState(null); // tracks which platform is loaded


  const payingParticipants = true;

  const [useMobileVersion, setUseMobileVersion] = useState(window.innerWidth <= 768);

  useEffect(() => {
    function handleResize() {
      setUseMobileVersion(window.innerWidth <= 768);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Added empty dependency array for better performance


  useEffect(() => {
    // Only fetch once we have a platform and session
    if (!platform || !sessionId) return;

    // If already fetched for this platform, don't refetch
    if (surveyLoadedForPlatform === platform) return;

    setSurveyLoading(true);

    // Reset existing questions to avoid rendering stale ones
    setDemographicSurveyQuestions([]);
    setUsageSurveyQuestions([]);
    setTechsavvinessSurveyQuestions([]);

    // Fetch demographics (platform=all) + usage (platform-specific)
    let successful = 0;
    let failed = false;

    const onSuccess = () => {
      successful += 1;
      if (successful === 2 && !failed) {
        setSurveyLoadedForPlatform(platform);
        setSurveyLoading(false);
      }
    };

    const onFail = () => {
      if (!failed) {
        failed = true;
        setSurveyLoading(false);
        ErrorConnectionFailed();
      }
    };

    FetchFromBackend(
      'questions/platform/chatbots/type/demographics',
      onSuccess,
      onFail,
      (q) => setDemographicSurveyQuestions(Array.isArray(q) ? q : [])
    );

    FetchFromBackend(
      `questions/platform/${platform}/type/platform-use`,
      onSuccess,
      onFail,
      (q) => setUsageSurveyQuestions(Array.isArray(q) ? q : [])
    );

}, [platform, sessionId, surveyLoadedForPlatform]);


  function moveToEnd() {
    setCurrentStep(steps.length - 1);
    window.scrollTo(0, 0);
  }

  function next() {
    setCurrentStep(currentStep + 1);
    window.scrollTo(0, 0);
  }

  function prev() {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  }

  // Define the steps data
  const steps = [
    {
      title: 'Select Platform',
      content: (
        <PickerPlatform
          next={next}
          payingParticipants={payingParticipants}
          setPlatform={setPlatform}
          setSessionId={setSessionId}
          setEmailIdentifier={setEmailIdentifier}
          useMobileVersion={useMobileVersion}
        />
      ),
    },
    {
      title: 'Consent Form',
      content: (
        <FormConsent
          next={next}
          prev={prev}
          platform={platform}
          setPlatform={setPlatform}
          sessionId={sessionId}
        />
      ),
    },
    {
      title: 'Survey',
      content: (
        <FormSurvey
          next={next}
          useMobileVersion={useMobileVersion}
          platform={platform}
          sessionId={sessionId}
          emailIdentifier={emailIdentifier}
          donationOptions={donationOptions}
          surveyLoading={surveyLoading}
          demographicSurveyQuestions={demographicSurveyQuestions}
          usageSurveyQuestions={usageSurveyQuestions}
          techsavvinessSurveyQuestions={techsavvinessSurveyQuestions}
          setAmountToBePayed={setAmountToBePayed}
          amountToBePayed={amountToBePayed}
        />
      ),
    },
    {
      title: 'Donation Options/Information',
      content: (
        <FormDonationOptions
          next={next}
          prev={prev}
          platform={platform}
          donationOptions={donationOptions}
          payingParticipants={payingParticipants}
          setDonationOptions={setDonationOptions}
          donationOptionsSet={donationOptionsSet}
          setDonationOptionsSet={setDonationOptionsSet}
          useMobileVersion={useMobileVersion}
        />
      ),
    },
    {
      title: 'Donation Review',
      content: (
        <DonationReview
          next={next}
          prev={prev}
          platform={platform}
          donationOptions={donationOptions}
        />
      ),
    },
    {
      title: 'File Upload',
      content: (
        <PageUpload
          next={next}
          prev={prev}
          moveToEnd={moveToEnd}
          payingParticipants={payingParticipants}
          donationOptions={donationOptions}
          sessionId={sessionId}
          platform={platform}
          emailIdentifier={emailIdentifier}
          amountToBePayed={amountToBePayed}
          setAmountToBePayed={setAmountToBePayed}
          userData={userData}
          setUserData={setUserData}
          setDemographicSurveyQuestions={setDemographicSurveyQuestions}
          setTechsavvinessSurveyQuestions={setTechsavvinessSurveyQuestions}
          setUsageSurveyQuestions={setUsageSurveyQuestions}
        />
      ),
    },
    {
      title: 'Done',
      content: (
        <PageDone
          payingParticipants={payingParticipants}
          emailIdentifier={emailIdentifier}
          amountToBePayed={amountToBePayed}
        />
      ),
    },
  ];

  // Map the internal 'steps' array to the format expected by Ant Design's 'items' prop --> this should fix that warning 
  const stepItems = steps.map((item) => ({
    key: item.title,
    title: item.title,
  }));

  return (
    <>
      <Steps
        current={currentStep}
        direction={useMobileVersion ? "vertical" : "horizontal"}
        labelPlacement="vertical"
        items={stepItems}
      />
      <div style={{ marginTop: '24px' }}>
        {steps[currentStep].content}
      </div>
    </>
  );
};